import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import { functions, logger, useAlert, useTheme, useUser } from "capsule"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ActivityIndicator, Linking, Pressable, StyleSheet, View } from "react-native"
import FastImage from "react-native-fast-image"
import InAppBrowser from "react-native-inappbrowser-reborn"
import { Text } from "react-native-paper"

import { images } from "../../assets/images"
import { isE2EEnvironment } from "../../features/config/environment"
import {
  buildE2ECallback,
  generateLoginUrl,
  getCallbackUrlScheme,
} from "../../features/config/franceConnect"
import { RegisterVerifyIdentityNS } from "../../features/i18n/constants"
import { updateUserData } from "../../features/models/UserDataFunctions"
import { OnboardingParamList } from "../../features/Navigation/types"

const RegisterVerifyIdentityScreen = () => {
  const {
    colors: {
      primary,
      white: { mediumEmphasis: white },
      surface: { background },
    },
    dimensions: { spacing },
  } = useTheme()
  const [loading, setLoading] = useState(false)
  const [sessionState, setSessionState] = useState<string | null>(null)
  const { showSnack } = useAlert()
  const { t } = useTranslation(RegisterVerifyIdentityNS)
  const { userDocRef } = useUser()
  const navigation =
    useNavigation<StackNavigationProp<OnboardingParamList, "RegisterVerifyIdentity">>()
  const s = useMemo(
    () => ({
      view: [
        styles.view,
        {
          backgroundColor: background,
          paddingHorizontal: spacing * 2,
        },
      ],
      section: styles.section,
      link: [styles.link, { color: primary }],
      text: styles.text,
      button: styles.button,
    }),
    [background, primary, spacing],
  )
  useEffect(() => {
    const subscription = Linking.addEventListener("url", async ({ url }) => {
      setLoading(false)
      if (!url.startsWith(getCallbackUrlScheme())) {
        return
      }
      InAppBrowser.close()

      const params = new URLSearchParams(url.split("?")[1])
      const success = params.get("success")

      setLoading(false)
      setSessionState(null)

      if (success === "true") {
        // Réconciliation réussie
        // userData déjà mis à jour côté backend avec fcSub

        // Naviguer vers l'écran de succès
        navigation.navigate("RegisterVerifyIdentitySuccess")
      } else {
        // Réconciliation échouée
        switch (params.get("error")) {
          case "access_denied":
            showSnack(t("reconciliationCancelled"))
            return
          case "server_error":
          case "invalid_request":
          case "invalid_scope":
          case "temporarily_unavailable":
            showSnack(t("reconciliationErrorRetry"))
            return
          default:
            showSnack(t("reconciliationError"))
            updateUserData(userDocRef, { profileNeedAnUpdate: true })
            return
        }
      }
    })

    return () => subscription.remove()
  }, [navigation, showSnack, t, sessionState, userDocRef])
  const handleStart = async () => {
    setLoading(true)
    try {
      // Appeler la Cloud Function qui génère le nonce avec authentification
      const { data: nonce } = await functions().httpsCallable<
        unknown,
        { nonce: string; state: string }
      >("generateNonce")()

      setSessionState(nonce.state)
      if (isE2EEnvironment()) {
        const callbackUrl = buildE2ECallback()
        try {
          const response = await fetch(callbackUrl, {
            method: "GET",
          })
          const json = await response.json()
          setTimeout(() => {
            Linking.openURL(json.url)
          }, 500)
        } catch (e) {
          showSnack(e.message)
          logger(e)
        } finally {
          setLoading(false)
        }

        return // Ne pas continuer avec le flow normal
      }

      // Flow normal (prod/preprod)
      const loginUrl = generateLoginUrl(nonce.nonce, nonce.state)
      await InAppBrowser.open(loginUrl, {
        dismissButtonStyle: "cancel",
        showTitle: true,
        ephemeralWebSession: true,
      })
      setLoading(false)
    } catch (e) {
      setLoading(false)
      showSnack(e.message)
      logger(e)
    }
  }
  return (
    <View style={s.view}>
      <View style={s.section}>
        <Text style={styles.title}>{t("title")}</Text>
        <Text style={styles.text}>{t("subTitle")}</Text>
        <Pressable
          style={s.button}
          onPress={handleStart}
          disabled={loading}
          testID="fc_verify_button"
        >
          <FastImage
            source={images.franceconnect_button as number}
            style={[styles.button, { opacity: loading ? 0.5 : 1 }]}
          />
          {loading && <ActivityIndicator size="large" color={white} style={styles.loading} />}
        </Pressable>
        <Pressable
          onPress={() => Linking.openURL("https://franceconnect.gouv.fr/france-connect-plus")}
        >
          <Text style={[s.link]}>{t("linkExplanation")}</Text>
        </Pressable>
      </View>
      <View style={s.section}>
        <Text style={styles.title}>{t("required.title")}</Text>
        <Text style={styles.text}>{t("required.description")}</Text>
        <Text style={styles.title}>{t("required.doesntHaveAccount")}</Text>

        <Pressable
          onPress={() =>
            Linking.openURL(
              "https://aide.franceconnect.gouv.fr/faq/comprendre-franceconnect/creation-compte/",
            )
          }
        >
          <Text style={s.link}>{t("required.createOne")}</Text>
        </Pressable>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  view: {
    flex: 1,
    gap: 40,
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
  },
  section: {
    gap: 12,
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
  },
  text: {
    fontSize: 12,
    textAlign: "center",
  },
  link: {
    fontSize: 14,
    textDecorationLine: "underline",
    textAlign: "center",
  },
  button: {
    height: 56,
    width: 245,
  },
  loading: {
    position: "absolute",
    height: 56,
    width: 245,
  },
})

export default RegisterVerifyIdentityScreen
